import {
  Component,
  ElementRef,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { IconComponent } from '../icon/icon.component';

/**
 * One selectable row in an `app-dropdown` panel.
 *
 * `value` is compared by identity (`===`) against the control's `value` input to
 * decide which row is selected, so option values must be primitives or stable
 * object references — not freshly-built objects.
 */
export interface DropdownOption<T> {
  label: string;
  value: T;
  /**
   * Rendered but not selectable — the row still appears so the filter's full
   * vocabulary stays visible (e.g. "Me" before the admin user id resolves),
   * it just can't be chosen.
   */
  disabled?: boolean;
}

/** Per-instance suffix for the ids that wire the trigger to its label/panel/options. */
let nextDropdownId = 0;

/**
 * Filter dropdown used across the queue filter bars in place of a native
 * `<select>`, whose popup is rendered by the OS and can't be styled to match
 * the design system (in particular it can't carry the selected-row treatment or
 * honour dark mode reliably).
 *
 * It implements the ARIA 1.2 combobox-with-listbox pattern rather than only
 * looking the part: the trigger is a real button carrying `role="combobox"` and
 * `aria-expanded`, the panel is a `role="listbox"` of `role="option"` rows, and
 * keyboard handling (arrows / Home / End / Enter / Escape / Tab) is implemented
 * here because replacing a native select otherwise loses all of it — this is a
 * staff PHI surface, so the control has to stay operable without a mouse.
 *
 * **Value contract.** The control is value-driven, not self-managing: it never
 * mutates `value`, it emits `valueChange` and waits for the parent to feed the
 * new value back (`[(value)]` works). `value` is `T | null`, so a consumer whose
 * `T` already includes `null` (e.g. `WorkflowStatusType | null`) cannot use
 * `null` to mean "nothing selected" — give that state an explicit option, as the
 * queue filter bars do with their "All" row. There is deliberately no
 * `ControlValueAccessor`: every consumer today binds signals directly, and a CVA
 * would add a second, divergent way to drive the same state.
 */
@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './dropdown.html',
  styleUrl: './dropdown.scss',
})
export class Dropdown<T> {
  /** Tallest the panel gets before it scrolls internally. */
  private static readonly MAX_PANEL_HEIGHT = 280;
  /** Gap between the trigger and the panel — matches the `--sp-4` offset in the SCSS. */
  private static readonly PANEL_GAP = 4;
  /** Breathing room kept between the panel and the edge that would clip it. */
  private static readonly EDGE_MARGIN = 8;
  /**
   * `overflow` values that cut the panel off. `hidden`/`clip` do it outright;
   * `auto`/`scroll`/`overlay` do too, because the panel is anchored to the
   * trigger and so can't be scrolled into view inside them. Matched positively
   * rather than as "anything but `visible`" so an environment that reports an
   * empty computed value is read as non-clipping instead of pinning the panel
   * to zero height.
   */
  private static readonly CLIPPING_OVERFLOW: ReadonlySet<string> = new Set([
    'hidden',
    'clip',
    'scroll',
    'auto',
    'overlay',
  ]);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly instanceId = `app-dropdown-${nextDropdownId++}`;
  private readonly triggerRef = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');

  /** Floating label above the trigger's top border, e.g. "Status". */
  readonly label = input.required<string>();
  readonly options = input<readonly DropdownOption<T>[]>([]);
  readonly value = input<T | null>(null);
  /**
   * Replaces `label` in the trigger's accessible name when the visible label is
   * too terse to stand alone ("Filter requests by status" vs "Status"). The
   * current selection is appended either way — see `labelledBy`.
   */
  readonly ariaLabel = input<string>();
  /** Greys out and blocks the whole control, e.g. while its data source loads. */
  readonly disabled = input(false);

  readonly valueChange = output<T>();

  readonly isOpen = signal(false);
  /**
   * Index into `options()` of the keyboard-highlighted row, or -1 for none.
   * Distinct from the selected value: arrowing through the panel moves this
   * without committing a selection (and so without reloading a PHI queue).
   */
  readonly activeIndex = signal(-1);
  /**
   * Panel height cap, measured from the space below the trigger each time it
   * opens. The panel is absolutely positioned inside the component rather than
   * in a CDK overlay, so it cannot escape an ancestor's `overflow: hidden` —
   * every page shell hosting these filter bars clips. Capping to the space
   * actually available (rather than a fixed 280px) means the panel scrolls
   * internally instead of having its bottom rows silently cut off. It never
   * flips above the trigger: the app header would clip it just as badly on the
   * other side.
   */
  readonly panelMaxHeight = signal(Dropdown.MAX_PANEL_HEIGHT);

  readonly panelId = `${this.instanceId}-panel`;
  readonly labelId = `${this.instanceId}-label`;
  readonly valueId = `${this.instanceId}-value`;
  readonly ariaLabelId = `${this.instanceId}-aria-label`;

  /**
   * The trigger is named by two elements — the label and the current value — so
   * a screen reader announces "Status, Needs Info" the way a native `<select>`
   * does. A plain `aria-label` would win over the button's contents and leave
   * the applied filter unannounced, which on these queues means the user cannot
   * tell which PHI rows are being hidden from them.
   */
  readonly labelledBy = computed(
    () => `${this.ariaLabel() ? this.ariaLabelId : this.labelId} ${this.valueId}`,
  );

  private readonly selectedOption = computed(
    () => this.options().find((option) => option.value === this.value()) ?? null,
  );

  /**
   * Text shown in the trigger. Falls back to an em dash when the current value
   * matches no option — including the empty-options case, where there is
   * nothing the trigger could name.
   */
  readonly displayValue = computed(() => this.selectedOption()?.label ?? '—');

  /** `aria-activedescendant` target: the highlighted row, or null when none. */
  readonly activeOptionId = computed(() => {
    const index = this.activeIndex();
    return this.isOpen() && index >= 0 ? this.optionId(index) : null;
  });

  optionId(index: number): string {
    return `${this.instanceId}-option-${index}`;
  }

  isSelected(option: DropdownOption<T>): boolean {
    return option.value === this.value();
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
      return;
    }

    this.open();
  }

  open(): void {
    if (this.isOpen() || this.disabled()) {
      return;
    }

    this.measurePanelHeight();
    this.isOpen.set(true);
    // Start keyboard navigation on the current selection so the first arrow
    // press moves from where the user is, not from the top of the list. A
    // disabled selection (e.g. sticky "Me" before the admin id resolves) is
    // skipped: highlighting a row Enter cannot commit is a dead end.
    const selectedIndex = this.options().findIndex((option) => option.value === this.value());
    this.activeIndex.set(this.options()[selectedIndex]?.disabled ? -1 : selectedIndex);
    // Safari does not focus a <button> on click, and every key handler lives on
    // the trigger — without this, a mouse-opened panel is inert to the keyboard.
    this.focusTrigger();
    this.scrollActiveIntoView();
  }

  close(): void {
    if (!this.isOpen()) {
      return;
    }

    this.isOpen.set(false);
    this.activeIndex.set(-1);
  }

  /**
   * Commit an option and close. Re-picking the current value emits nothing:
   * a native `<select>` fires no change event in that case either, and every
   * consumer reloads a PHI-bearing queue from `valueChange`.
   */
  selectOption(option: DropdownOption<T>): void {
    if (option.disabled) {
      return;
    }

    const isUnchanged = option.value === this.value();
    this.close();
    // Clicking a row moves focus off the trigger (an <li> is not focusable), so
    // put it back — the ARIA combobox pattern keeps focus on the control, and
    // otherwise the next Tab restarts from the top of the document.
    this.focusTrigger();

    if (!isUnchanged) {
      this.valueChange.emit(option.value);
    }
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      // Arrows would otherwise scroll the page behind the open panel.
      event.preventDefault();

      if (!this.isOpen()) {
        this.open();
        return;
      }

      this.moveActive(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if ((event.key === 'Home' || event.key === 'End') && this.isOpen()) {
      // Parity with a native <select>, which jumps to the ends of the list.
      event.preventDefault();
      this.moveToEnd(event.key === 'Home' ? 1 : -1);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      // While closed, let the button's own activation open the panel via
      // (click) — handling it here as well would open and immediately re-close.
      if (!this.isOpen()) {
        return;
      }

      // preventDefault stops the button firing its synthetic click, which would
      // toggle the panel straight back open after this selection closes it.
      event.preventDefault();
      const active = this.options()[this.activeIndex()];

      // An active row that is disabled (or absent) commits nothing, but the key
      // still has to do something — closing beats leaving the panel hanging
      // open with no feedback.
      if (active && !active.disabled) {
        this.selectOption(active);
        return;
      }

      this.close();
      this.focusTrigger();
      return;
    }

    if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      this.close();
      this.focusTrigger();
      return;
    }

    if (event.key === 'Tab') {
      // Focus is leaving the control, and the panel is anchored to the trigger,
      // so it must not stay open over whatever the user moves to.
      this.close();
    }
  }

  /**
   * Closes the panel on any click outside this component. This is also what
   * keeps a single panel open at a time: clicking a second dropdown's trigger
   * bubbles to the document, where every *other* instance sees a target outside
   * itself and closes. The clicked instance has already toggled itself open by
   * the time this runs, and sees the target inside itself, so it stays open.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) {
      return;
    }

    const target = event.target;
    if (target instanceof Node && this.host.nativeElement.contains(target)) {
      return;
    }

    this.close();
  }

  /**
   * Second dismissal path, because the click one is not sufficient on its own:
   * several controls in this app stop click propagation before it reaches the
   * document (the header user menu, modal content), which would otherwise leave
   * an open panel stranded on screen. Focus moving out of the component is the
   * signal those cases share, and it also covers keyboard-only focus loss.
   */
  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    if (!this.isOpen()) {
      return;
    }

    const next = event.relatedTarget;
    // A null relatedTarget means focus went nowhere trackable (a click on a
    // non-focusable element, or the window losing focus). Selecting a row is
    // exactly that case and already re-focuses the trigger, so treating null as
    // "still inside" avoids closing the panel out from under an in-flight click.
    if (next === null || (next instanceof Node && this.host.nativeElement.contains(next))) {
      return;
    }

    this.close();
  }

  /** Re-measure while open, so a resize or zoom change can't leave a clipped panel. */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen()) {
      this.measurePanelHeight();
    }
  }

  /** Move the highlight by `delta`, wrapping and skipping disabled rows. */
  private moveActive(delta: number): void {
    const options = this.options();
    if (options.length === 0) {
      return;
    }

    // Nothing highlighted yet (the current value matched no option, or matched a
    // disabled one): enter the list at the end the arrow points at, rather than
    // relative to a -1 that would land two rows in.
    let index = this.activeIndex();
    if (index < 0) {
      index = delta > 0 ? -1 : 0;
    }

    // Bounded by the option count so an all-disabled list can't spin forever.
    for (let step = 0; step < options.length; step++) {
      index = (index + delta + options.length) % options.length;

      if (!options[index].disabled) {
        this.activeIndex.set(index);
        this.scrollActiveIntoView();
        return;
      }
    }
  }

  /**
   * Highlight the first (`direction` 1) or last (`direction` -1) enabled option.
   * Seeded just outside the list so the shared scan below walks inwards.
   */
  private moveToEnd(direction: 1 | -1): void {
    const options = this.options();
    if (options.length === 0) {
      return;
    }

    this.activeIndex.set(direction > 0 ? -1 : 0);
    this.moveActive(direction);
  }

  /**
   * Keep the highlighted row inside the scrollable panel. Deferred to the next
   * render because the row for a freshly-set `activeIndex` (or a panel that has
   * only just opened) does not exist in the DOM yet.
   */
  private scrollActiveIntoView(): void {
    afterNextRender(
      () => {
        const id = this.activeOptionId();
        if (!id) {
          return;
        }

        // Optional call: jsdom does not implement scrollIntoView.
        this.host.nativeElement.querySelector<HTMLElement>(`#${id}`)?.scrollIntoView?.({
          block: 'nearest',
        });
      },
      { injector: this.injector },
    );
  }

  private focusTrigger(): void {
    this.triggerRef().nativeElement.focus();
  }

  /** Cap the panel to the space between the trigger and the edge that would clip it. */
  private measurePanelHeight(): void {
    const available =
      this.clipBottom() -
      this.triggerRef().nativeElement.getBoundingClientRect().bottom -
      Dropdown.PANEL_GAP -
      Dropdown.EDGE_MARGIN;

    // Deliberately no minimum height: a floor that outran `available` would put
    // the bottom rows back under a clip with no way to scroll to them, which is
    // the exact failure this measurement exists to prevent. When the space
    // really is that small, a cramped panel that scrolls internally is the
    // honest outcome.
    this.panelMaxHeight.set(Math.min(Dropdown.MAX_PANEL_HEIGHT, Math.max(0, available)));
  }

  /**
   * Lowest point on screen the panel can occupy: the bottom of the viewport,
   * tightened by any ancestor that clips its overflow.
   *
   * The viewport alone is not enough. It happens to be the right answer for the
   * queue pages, whose host is `height: calc(100dvh - header)` and so always
   * reaches the viewport bottom, but the Audit Log's host pairs `max-height`
   * with `overflow: hidden` so the card can hug its content — whenever the table
   * is short (loading, empty, error, a couple of rows) that host's box ends well
   * above the fold, and a panel measured against the viewport has its bottom
   * rows cut off by the host with no way to reach them.
   *
   * `:host` is positioned, so the panel's containing block is the host itself
   * and every ancestor's overflow applies to it — hence the walk rather than a
   * single lookup.
   */
  private clipBottom(): number {
    let bottom = window.innerHeight;

    for (
      let ancestor = this.host.nativeElement.parentElement;
      // The root element's own overflow propagates to the viewport, which
      // `window.innerHeight` already accounts for.
      ancestor && ancestor !== document.documentElement;
      ancestor = ancestor.parentElement
    ) {
      // One axis is enough to test: a non-visible `overflow-x` forces the
      // computed `overflow-y` to `auto`.
      if (!Dropdown.CLIPPING_OVERFLOW.has(getComputedStyle(ancestor).overflowY)) {
        continue;
      }

      bottom = Math.min(bottom, ancestor.getBoundingClientRect().bottom);
    }

    return bottom;
  }
}
